import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  DEFAULT_SHOPIFY_PRODUCT_MATCH_MAPPING,
  ShopifyProductMatchMapping,
} from '@domain/connection/models/ConnectionSettings';
import {
  getDuplicateSourceValues,
  getImportBannerTone,
  suggestTargetForSource,
  ShopifyImportBannerTone,
  ShopifyMatchedJuststockProduct,
  ShopifySetupProductPreview,
} from '@domain/connection/models/ShopifyMatchPreview';

import { CustomField } from '@domain/product/models/CustomField';
import { useCustomFields } from '@features/products/hooks/useCustomFields';
import { APP_CONFIG } from '@config/app.config';
import {
  SHOPIFY_PRODUCT_SOURCE_FIELDS,
  ShopifyProductSourceFieldOption,
  customFieldTargetLabel,
  customFieldTargetPath,
  JUSTSTOCK_PRODUCT_TARGET_FIELDS,
} from '@shared/connection/shopify/constants/shopifyProductFields';

const CREATE_CUSTOM_FIELD_VALUE = '__create_custom_field__';

function toCustomFieldKey(label: string): string {
  return label.trim().replace(/\s+/g, '_').replace(/[^A-Za-z0-9_]/g, '');
}

interface ShopifyProductMappingFormProps {
  mapping: ShopifyProductMatchMapping;
  onMappingChange: (mapping: ShopifyProductMatchMapping) => void;
  fieldCounts: Record<string, number>;
  productsScanned: number;
  matchesFound: number;
  matchedProducts?: Record<string, ShopifyMatchedJuststockProduct>;
  products: ShopifySetupProductPreview[];
  customFields?: CustomField[];
  disabled?: boolean;
  marketplaceLabel?: string;
  sourceFields?: ShopifyProductSourceFieldOption[];
  suggestTarget?: (sourceField: string, currentTarget?: string) => string;
}

const BANNER_STYLES: Record<ShopifyImportBannerTone, { bg: string; buttonBg: string }> = {
  info: {
    bg: APP_CONFIG.ui.theme.colors.primary,
    buttonBg: 'rgba(255, 255, 255, 0.18)',
  },
  warning: {
    bg: APP_CONFIG.ui.theme.colors.warning,
    buttonBg: 'rgba(255, 255, 255, 0.2)',
  },
  error: {
    bg: APP_CONFIG.ui.theme.colors.error,
    buttonBg: 'rgba(255, 255, 255, 0.2)',
  },
};

function sourceFieldLabel(path: string, sourceFields: ShopifyProductSourceFieldOption[]): string {
  return sourceFields.find((field) => field.path === path)?.label ?? path;
}

function buildBannerMessage(
  marketplaceLabel: string,
  tone: ShopifyImportBannerTone,
  sourceLabel: string,
  targetLabel: string,
  productsFound: number,
  productsScanned: number,
  newProducts: number,
  duplicateValueCount: number,
): string {
  if (duplicateValueCount > 0) {
    return `${duplicateValueCount} ${sourceLabel.toLowerCase()} value${duplicateValueCount === 1 ? ' is' : 's are'} used on multiple ${marketplaceLabel} products. Matching requires unique values — fix the duplicates in ${marketplaceLabel} or choose a different field.`;
  }

  if (tone === 'error') {
    return `No products have ${sourceLabel} set. Choose a different field or fill this field in ${marketplaceLabel} before importing.`;
  }

  if (tone === 'warning') {
    return `With this setting we link the ${marketplaceLabel} ${sourceLabel.toLowerCase()} to ${targetLabel} and import ${productsFound} of ${productsScanned} products. ${productsScanned - productsFound} products are missing this field.`;
  }

  return `With this setting we link the ${marketplaceLabel} ${sourceLabel.toLowerCase()} to ${targetLabel} and import ${newProducts} new products`;
}

const ShopifyProductMappingForm: React.FC<ShopifyProductMappingFormProps> = ({
  mapping,
  onMappingChange,
  fieldCounts,
  productsScanned,
  matchesFound,
  matchedProducts = {},
  products,
  customFields = [],
  disabled = false,
  marketplaceLabel = 'Shopify',
  sourceFields = SHOPIFY_PRODUCT_SOURCE_FIELDS,
  suggestTarget = suggestTargetForSource,
}) => {
  const [productsDialogOpen, setProductsDialogOpen] = useState(false);
  const [createFieldOpen, setCreateFieldOpen] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [createFieldError, setCreateFieldError] = useState<string | null>(null);
  const [isCreatingField, setIsCreatingField] = useState(false);
  const { createCustomField } = useCustomFields();

  const productsFound = fieldCounts[mapping.sourceField] ?? 0;
  const newProducts = Math.max(productsFound - matchesFound, 0);
  const duplicateValues = getDuplicateSourceValues(products, mapping.sourceField);
  const tone = getImportBannerTone(productsFound, productsScanned, duplicateValues.size > 0);
  const bannerStyle = BANNER_STYLES[tone];

  const targetFieldOptions = [
    ...JUSTSTOCK_PRODUCT_TARGET_FIELDS,
    ...customFields.map((field) => ({
      path: customFieldTargetPath(field.key),
      label: customFieldTargetLabel(field.label),
    })),
  ];

  if (
    mapping.targetField &&
    !targetFieldOptions.some((field) => field.path === mapping.targetField)
  ) {
    targetFieldOptions.push({
      path: mapping.targetField,
      label: mapping.targetField.startsWith('customFields.')
        ? mapping.targetField.slice('customFields.'.length)
        : mapping.targetField,
    });
  }

  const selectedSourceLabel = sourceFieldLabel(mapping.sourceField, sourceFields);
  const selectedTargetLabel = targetFieldOptions.find(
    (field) => field.path === mapping.targetField,
  )?.label ?? mapping.targetField;

  const sourceFieldValue = sourceFields.some((field) => field.path === mapping.sourceField)
    ? mapping.sourceField
    : sourceFields[0]?.path ?? DEFAULT_SHOPIFY_PRODUCT_MATCH_MAPPING.sourceField;

  const previewProducts = products.filter((product) => {
    const value = product.fieldValues[mapping.sourceField];
    return value != null && value.trim() !== '';
  });

  const handleSourceChange = (value: string) => {
    onMappingChange({
      sourceField: value,
      targetField: suggestTarget(value, mapping.targetField),
    });
  };

  const openCreateCustomField = () => {
    setCreateFieldError(null);
    setNewFieldLabel(selectedSourceLabel);
    setCreateFieldOpen(true);
  };

  const handleTargetChange = (value: string) => {
    if (value === CREATE_CUSTOM_FIELD_VALUE) {
      openCreateCustomField();
      return;
    }

    onMappingChange({
      ...mapping,
      targetField: value,
    });
  };

  const handleCreateCustomField = async () => {
    const label = newFieldLabel.trim();
    const key = toCustomFieldKey(label);
    if (!label || !key) {
      setCreateFieldError('Enter a field name');
      return;
    }
    if (customFields.some((field) => field.key === key)) {
      setCreateFieldError('A custom field with this name already exists');
      return;
    }

    setIsCreatingField(true);
    setCreateFieldError(null);
    try {
      const created = await createCustomField({
        key,
        label,
        applyToAllProducts: true,
      });
      onMappingChange({
        ...mapping,
        targetField: customFieldTargetPath(created.key),
      });
      setCreateFieldOpen(false);
      setNewFieldLabel('');
    } catch (error) {
      setCreateFieldError(error instanceof Error ? error.message : 'Failed to create custom field');
    } finally {
      setIsCreatingField(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            {marketplaceLabel}
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={sourceFieldValue}
              onChange={(event) => handleSourceChange(event.target.value)}
              disabled={disabled}
            >
              {sourceFields.map((field) => (
                <MenuItem key={field.path} value={field.path}>
                  {field.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {productsFound} products found
          </Typography>

        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pt: { xs: 0, sm: 3.5 },
            px: { xs: 0, sm: 0.5 },
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
            Link to
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            JustStock
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={mapping.targetField || DEFAULT_SHOPIFY_PRODUCT_MATCH_MAPPING.targetField}
              onChange={(event) => handleTargetChange(event.target.value)}
              disabled={disabled || isCreatingField}
            >
              {targetFieldOptions.map((field) => (
                <MenuItem key={field.path} value={field.path}>
                  {field.label}
                </MenuItem>
              ))}
              <MenuItem value={CREATE_CUSTOM_FIELD_VALUE}>
                <Typography variant="body2" color="primary">
                  Create new custom field…
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {matchesFound} matches found
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1.5,
          borderRadius: 1,
          bgcolor: bannerStyle.bg,
          color: '#FFFFFF',
        }}
      >
        <Typography variant="body2" sx={{ flex: 1, color: 'inherit' }}>
          {buildBannerMessage(
            marketplaceLabel,
            tone,
            selectedSourceLabel,
            selectedTargetLabel,
            productsFound,
            productsScanned,
            newProducts,
            duplicateValues.size,
          )}
        </Typography>

        {tone !== 'error' && (
          <Button
            size="small"
            onClick={() => setProductsDialogOpen(true)}
            sx={{
              flexShrink: 0,
              color: '#FFFFFF',
              bgcolor: bannerStyle.buttonBg,
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.28)' },
            }}
          >
            View products
          </Button>
        )}
      </Box>

      <Dialog
        open={productsDialogOpen}
        onClose={() => setProductsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${APP_CONFIG.ui.theme.colors.border}`,
          }}
        >
          <Typography variant="h6">Products to import</Typography>
          <IconButton onClick={() => setProductsDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              gap: 2,
              px: 3,
              py: 1.5,
              borderBottom: `1px solid ${APP_CONFIG.ui.theme.colors.border}`,
              bgcolor: 'background.default',
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight="medium">
              {marketplaceLabel}
            </Typography>
            <Box />
            <Typography variant="caption" color="text.secondary" fontWeight="medium">
              JustStock
            </Typography>
          </Box>
          <List dense disablePadding>
            {previewProducts.map((product, index) => {
              const matchValue = product.fieldValues[mapping.sourceField]?.trim() || null;
              const matched = matchValue ? matchedProducts[matchValue] : undefined;
              const isDuplicate = !!matchValue && duplicateValues.has(matchValue);

              return (
                <ListItem
                  key={`${product.title}-${index}`}
                  alignItems="center"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    gap: 2,
                    px: 3,
                    py: 1.5,
                    borderBottom: `1px solid ${APP_CONFIG.ui.theme.colors.border}`,
                    bgcolor: isDuplicate ? 'error.light' : undefined,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    {product.imageUrl ? (
                      <Box
                        component="img"
                        src={product.imageUrl}
                        alt={product.title}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          objectFit: 'cover',
                          border: 1,
                          borderColor: 'divider',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: 'background.default',
                          border: 1,
                          borderColor: 'divider',
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{product.title}</Typography>
                      <Typography
                        variant="caption"
                        color={isDuplicate ? 'error.main' : 'text.secondary'}
                        noWrap
                      >
                        {selectedSourceLabel}: {matchValue}
                        {isDuplicate ? ' · Not unique' : ''}
                      </Typography>
                    </Box>
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                    →
                  </Typography>

                  {isDuplicate ? (
                    <Typography variant="body2" color="error.main">
                      Duplicate value
                    </Typography>
                  ) : matched ? (
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                      {matched.image ? (
                        <Box
                          component="img"
                          src={matched.image}
                          alt={matched.title}
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            objectFit: 'cover',
                            border: 1,
                            borderColor: 'divider',
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            bgcolor: 'background.default',
                            border: 1,
                            borderColor: 'divider',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{matched.title}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {selectedTargetLabel}: {matchValue}
                        </Typography>
                      </Box>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      New product
                    </Typography>
                  )}
                </ListItem>
              );
            })}
            {previewProducts.length === 0 && (
              <ListItem>
                <ListItemText primary="No products found for this field" />
              </ListItem>
            )}
          </List>
        </DialogContent>
      </Dialog>

      <Dialog
        open={createFieldOpen}
        onClose={() => !isCreatingField && setCreateFieldOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${APP_CONFIG.ui.theme.colors.border}`,
          }}
        >
          <Typography variant="h6">Create custom field</Typography>
          <IconButton onClick={() => setCreateFieldOpen(false)} disabled={isCreatingField}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This field will be used to match {marketplaceLabel} {selectedSourceLabel.toLowerCase()} values in JustStock.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Field name"
              value={newFieldLabel}
              onChange={(event) => setNewFieldLabel(event.target.value)}
              disabled={isCreatingField}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleCreateCustomField();
                }
              }}
            />
            {createFieldError && (
              <Alert severity="error">{createFieldError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${APP_CONFIG.ui.theme.colors.border}` }}>
          <Button onClick={() => setCreateFieldOpen(false)} disabled={isCreatingField}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateCustomField}
            disabled={isCreatingField || !newFieldLabel.trim()}
            startIcon={isCreatingField ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isCreatingField ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ShopifyProductMappingForm;
